import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { pollVerificationStatus, clearVerification } from '../store/slices/emailVerificationSlice';
import { fetchCurrentUser } from '../store/slices/authSlice';
import { fetchResults, patchEmailResultsBatch } from '../store/slices/resultsSlice';

interface UseEmailVerificationPollingOptions {
  onComplete?: (results: any) => void;
  onError?: (error: string) => void;
  searchId?: number;
}

const IDLE_TIMEOUT_MS = 45_000;
const IDLE_TIMEOUT_BATCH_MS = 90_000; // batch-sync chunks can take 20–30s each

function adaptiveInterval(total: number): number {
  if (total <= 5) return 1000;
  if (total <= 15) return 1500;
  return 2500;
}

export const useEmailVerificationPolling = (options?: UseEmailVerificationPollingOptions) => {
  const dispatch = useAppDispatch();
  const { activeVerification, completedVerification, error, loading } = useAppSelector(
    (state: any) => state.emailVerification
  );
  const { pagination, filters } = useAppSelector((state: any) => state.results);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const consecutiveErrorsRef = useRef(0);
  const lastProgressTimeRef = useRef<number>(Date.now());
  const onCompleteRef = useRef(options?.onComplete);
  const onErrorRef = useRef(options?.onError);
  const searchIdRef = useRef(options?.searchId);
  const isOnlineRef = useRef(true);
  const hasRefreshedRef = useRef(false);

  // Refs to carry latest pagination/filters into the debounced refresh without
  // making doRefreshResults a dep of the polling effect.
  const paginationRef = useRef(pagination);
  const filtersRef = useRef(filters);
  const appliedEmailIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    onCompleteRef.current = options?.onComplete;
    onErrorRef.current = options?.onError;
    searchIdRef.current = options?.searchId ?? activeVerification?.searchId;
  }, [options?.onComplete, options?.onError, options?.searchId, activeVerification?.searchId]);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Reset per-batch state when a new verification request starts.
  const requestId = activeVerification?.requestId ?? null;
  useEffect(() => {
    appliedEmailIdsRef.current = new Set();
    hasRefreshedRef.current = false;
  }, [requestId]);

  useEffect(() => {
    const handleOnline = () => { isOnlineRef.current = true; };
    const handleOffline = () => { isOnlineRef.current = false; };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const doRefreshResults = useCallback(() => {
    if (!searchIdRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const { page, itemsPerPage } = paginationRef.current;
      dispatch(fetchResults({
        searchId: searchIdRef.current!,
        page,
        limit: itemsPerPage,
        filters: filtersRef.current,
        silent: true,
      }));
    }, 1500);
  }, [dispatch]);

  // Polling effect. Deps: only the values that should start/stop the loop —
  // requestId (new batch) and a boolean for whether polling is active.
  // We intentionally exclude the full activeVerification object to prevent the
  // effect from re-running on every poll response, which previously created a
  // runaway polling loop that fired as fast as the network would allow.
  const isActive = !!activeVerification &&
    activeVerification.status !== 'completed' &&
    activeVerification.status !== 'failed';

  useEffect(() => {
    if (!isActive || !requestId) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      consecutiveErrorsRef.current = 0;
      lastProgressTimeRef.current = Date.now();
      return;
    }

    consecutiveErrorsRef.current = 0;
    lastProgressTimeRef.current = Date.now();

    const interval = adaptiveInterval(activeVerification?.total ?? 0);

    const poll = async () => {
      if (!isOnlineRef.current) return;

      try {
        const result = await dispatch(pollVerificationStatus(requestId)).unwrap();
        consecutiveErrorsRef.current = 0;

        const processed = result.processed ?? 0;
        if (processed > 0) lastProgressTimeRef.current = Date.now();

        if (result.results && Array.isArray(result.results)) {
          const newPatches: Array<{
            email_id: number;
            email_type: 'person' | 'place';
            status: string;
            verified_at: string;
          }> = [];
          const verifiedAt = new Date().toISOString();

          for (const r of result.results) {
            if (!r.email_id || !r.status) continue;
            if (appliedEmailIdsRef.current.has(r.email_id)) continue;
            appliedEmailIdsRef.current.add(r.email_id);
            newPatches.push({
              email_id: r.email_id,
              email_type: (r.email_table === 'place' ? 'place' : 'person') as 'person' | 'place',
              status: r.status,
              verified_at: verifiedAt,
            });
          }

          if (newPatches.length > 0) {
            dispatch(patchEmailResultsBatch(newPatches));
          }
        }

        const isBatchRun = (activeVerification?.total ?? 0) > 50;
        const idleThreshold = isBatchRun ? IDLE_TIMEOUT_BATCH_MS : IDLE_TIMEOUT_MS;
        if (Date.now() - lastProgressTimeRef.current > idleThreshold) {
          if (onErrorRef.current) {
            onErrorRef.current(
              'Taking longer than usual — your credits are safe and results will appear here or in your search when ready.',
            );
          }
        }
      } catch {
        consecutiveErrorsRef.current += 1;
        if (consecutiveErrorsRef.current >= 3 && onErrorRef.current) {
          onErrorRef.current('Having trouble checking verification status. Will keep trying.');
          consecutiveErrorsRef.current = 0;
        }
      }
    };

    poll();
    intervalRef.current = setInterval(poll, interval);

    return () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };
  }, [requestId, isActive, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (completedVerification && !hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      dispatch(fetchCurrentUser());
      doRefreshResults();
      if (onCompleteRef.current) onCompleteRef.current(completedVerification);
    }
  }, [completedVerification, dispatch, doRefreshResults]);

  return {
    activeVerification,
    completedVerification,
    error,
    loading,
    isPolling: !!activeVerification && activeVerification.status === 'processing',
    clearVerification: () => dispatch(clearVerification()),
  };
};
